<template>
  <!-- La classe "show" doit être présente pour Bootstrap -->
  <div
    class="toast show"
    :class="[
      variant,
      customClass,
      {
        enter: isEntering,
        exit: isExiting,
      },
    ]"
  >
    <div
      v-if="!showIcon"
      class="status-bar"
    />
    <div
      v-else
      class="status-icon"
    >
      <component :is="icon" />
    </div>
    <div class="text-container">
      <strong
        class="text-regular bold"
        v-html="title"
      />
      <p
        class="content text-medium"
        v-html="content"
      />
    </div>
    <button
      v-if="!hideCloseBtn"
      class="button"
      @click="$emit('close')"
    >
      <UilTimes size="24" />
    </button>
  </div>
</template>

<script>
import {
  UilTimes,
  UilInfoCircle,
  UilExclamationOctagon,
  UilExclamationTriangle,
  UilThumbsUp,
} from "@iconscout/vue-unicons";

/**
 * Affiche un toast de notification à l'utilisateur.
 * Pour l'afficher :
 * this.$toast.info($props);
 * this.$toast.success($props);
 * this.$toast.warning($props);
 * this.$toast.error($props);
 */
export default {
  name: "Toast",
  components: {
    UilTimes,
  },
  props: {
    /**
     * Définit si on déclenche l'animation d'entrée.
     */
    isEntering: {
      type: Boolean,
      default: true,
    },
    /**
     * Définit si on déclenche l'animation de sortie.
     */
    isExiting: {
      type: Boolean,
      default: true,
    },
    /**
     * Définit une apparence alternative (bordure, fond...).
     */
    variant: {
      type: String,
      validator: (value) => ["info", "error", "success", "warning"].includes(value),
      default: "info",
    },
    /**
     * Classe personnalisée.
     */
    customClass: {
      type: String,
      default: null,
    },
    /**
     * Affiche une icône au lieu d'une ligne verticale.
     */
    showIcon: {
      type: Boolean,
      default: true,
    },
    /**
     * Titre du toast.
     */
    title: {
      type: String,
      default() { return this.defaultTitle; },
    },
    /**
     * Description principale du toast.
     */
    content: {
      type: String,
      default: null,
    },
    /**
     * Masque le bouton de fermeture.
     */
    hideCloseBtn: {
      type: Boolean,
      default: false,
    },
    /**
     * Durée avant la fermeture automatique du toast.
     */
    delay: {
      type: Number,
      // Durée par défaut de 5 secondes
      // Cas particulier: Si variant === success, alors 3 secondes
      default: function() {
        return this.variant === "success" ? 3000 : 5000;
      },
    },
  },
  emits: [
    /**
     * Déclenché au clic sur le bouton de fermeture du toast.
     */
    "close",
  ],
  computed: {
    /**
     * Un prop dépend d'un autre donc on passe par un computed.
     */
    defaultTitle() {
      return this.variant === "error" ? this.$t("info.une-erreur-est-survenue") : null;
    },
    icon() {
      let icon;
      switch (this.variant) {
        case "info":
          icon = UilInfoCircle;
          break;
        case "error":
          icon = UilExclamationOctagon;
          break;
        case "success":
          icon = UilThumbsUp;
          break;
        case "warning":
          icon = UilExclamationTriangle;
          break;
        default:
          icon = UilInfoCircle;
      }
      return icon;
    },
  },
};
</script>

<style lang="scss">
@use "../../assets/styles/components/infos/toast.scss";
</style>
